import React, { useState } from 'react';
import { User, Mail, Lock, Phone, Calendar } from 'lucide-react';
import API from '../api';
import { useNavigate } from 'react-router-dom';
import MessageModal from '../components/smallComponent/MessageModal'; // path check kar lena
import { useCustomers } from './context/CustomerContext';

export default function Register({ setUser }) {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
    const { fetchCustomers } = useCustomers();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    department: '',
    position: '',
    phone: '',
    hire_date: '',
    role: 'employee',
  });

  const [modal, setModal] = useState({
    show: false,
    message: '',
    type: 'info',
    title: 'Message',
  });

  const showModal = (message, type = 'info', title = 'Message') => {
    setModal({ show: true, message, type, title });
  };

  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        // ✅ Login flow
        const res = await API.post('/auth/login', {
          email: formData.email,
          password: formData.password,
        });

        if (!res.data.employee?.is_active) {
          showModal(
            'Your account is not active. Please contact admin.',
            'warning',
            'Inactive Account'
          );
          setLoading(false);
          return;
        }

        // ✅ Save token & user info
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('employee', JSON.stringify(res.data.employee));

        // ✅ Notify App.js instantly about login
        if (setUser) setUser(res.data.employee);
        window.dispatchEvent(new Event("storage")); // Trigger storage event for App.js

        navigate('/dashboard');
        fetchCustomers();
      } else {
        // ✅ Registration flow
        await API.post('/auth/register', formData);

        showModal('Account created! Please sign in.', 'success', 'Success');
        setIsLogin(true);
      }
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Something went wrong';
      showModal(message, 'error', 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="../../image/shubham-infotech.png"
            alt="Logo"
            className="h-16 mx-auto mb-4"
          />
          <p className="text-gray-500 mt-2">
            {isLogin ? 'Sign in to your account' : 'Create your account'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* Email */}
          <div className="relative">
            <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="email"
              name="email"
              placeholder="Email address"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Password */}
          <div className="relative">
            <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleInputChange}
              required
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Registration fields */}
          {!isLogin && (
            <>
              <div className="relative">
                <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="full_name"
                  placeholder="Full Name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Department</option>
                  <option value="Sales">Sales</option>
                  <option value="Developer">Developer</option>
                  <option value="Management">Management</option>
                </select>

                <input
                  type="text"
                  name="position"
                  placeholder="Position"
                  value={formData.position}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="relative">
                <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="relative">
                <label
                  htmlFor="hire_date"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Hire Date
                </label>
                <Calendar className="absolute left-3 top-10 w-5 h-5 text-gray-400" />
                <input
                  id="hire_date"
                  type="date"
                  name="hire_date"
                  value={formData.hire_date}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="employee">Employee</option>
              </select>
            </>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 transition"
          >
            {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => setIsLogin(!isLogin)}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
          </button>
        </div>
      </div>

      {/* Modal for messages */}
      <MessageModal
        show={modal.show}
        onClose={() => setModal({ ...modal, show: false })}
        message={modal.message}
        type={modal.type}
        title={modal.title}
      />
    </div>
  );
}





// import React, { useState } from 'react';
// import { User, Mail, Lock, Phone, Calendar, Info } from 'lucide-react';
// import API from '../api';
// import { useNavigate } from 'react-router-dom';
// import MessageModal from '../components/smallComponent/MessageModal'; 
// import { useCustomers } from './context/CustomerContext';

// export default function Register({ setUser }) {
//   const [isLogin, setIsLogin] = useState(true);
//   const [loading, setLoading] = useState(false);
//   const { fetchCustomers } = useCustomers();
//   const navigate = useNavigate();

//   const [formData, setFormData] = useState({
//     email: '',
//     password: '',
//     full_name: '',
//     department: '',
//     position: '',
//     phone: '',
//     hire_date: '',
//     role: 'employee',
//   });

//   const [modal, setModal] = useState({
//     show: false,
//     message: '',
//     type: 'info',
//     title: 'Message',
//   });

//   const showModal = (message, type = 'info', title = 'Message') => {
//     setModal({ show: true, message, type, title });
//   };

//   const handleInputChange = (e) => {
//     setFormData((prev) => ({
//       ...prev,
//       [e.target.name]: e.target.value,
//     }));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);

//     try {
//       if (isLogin) {
//         const res = await API.post('/auth/login', {
//           email: formData.email,
//           password: formData.password,
//         });

//         if (!res.data.employee?.is_active) {
//           showModal(
//             'Your account is not active. Please contact admin.',
//             'warning',
//             'Inactive Account'
//           );
//           setLoading(false);
//           return;
//         }

//         localStorage.setItem('token', res.data.token);
//         localStorage.setItem('employee', JSON.stringify(res.data.employee));

//         if (setUser) setUser(res.data.employee);
//         window.dispatchEvent(new Event("storage"));

//         navigate('/dashboard');
//         fetchCustomers();
//       } else {
//         await API.post('/auth/register', formData);
//         showModal('Account created! Please sign in.', 'success', 'Success');
//         setIsLogin(true);
//       }
//     } catch (error) {
//       const message =
//         error.response?.data?.message ||
//         error.response?.data?.error ||
//         error.message ||
//         'Something went wrong';
//       showModal(message, 'error', 'Error');
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
//       <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
//         <div className="text-center mb-8">
//           <img
//             src="../../image/shubham-infotech.png"
//             alt="Logo"
//             className="h-16 mx-auto mb-4"
//           />
//           <p className="text-gray-500 mt-2">
//             {isLogin ? 'Sign in to your account' : 'Create your account'}
//           </p>
//         </div>

//         {/* ✅ Demo credentials info */}
//         {isLogin && (
//           <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-gray-700">
//             <div className="flex items-center mb-1 text-blue-600 font-semibold">
//               <Info className="w-4 h-4 mr-1" /> Demo Login Details:
//             </div>
//             <ul className="list-disc ml-5 space-y-1">
//               <li><b>Admin:</b> admin@gmail.com / admin123</li>
//               <li><b>Sales:</b> sales@gmail.com / sales123</li>
//               <li><b>Developer:</b> developer@gmail.com / developer123</li>
//             </ul>
//           </div>
//         )}

//         <form onSubmit={handleSubmit} className="space-y-4" noValidate>
//           {/* Email */}
//           <div className="relative">
//             <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
//             <input
//               type="email"
//               name="email"
//               placeholder="Email address"
//               value={formData.email}
//               onChange={handleInputChange}
//               required
//               className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
//             />
//           </div>

//           {/* Password */}
//           <div className="relative">
//             <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
//             <input
//               type="password"
//               name="password"
//               placeholder="Password"
//               value={formData.password}
//               onChange={handleInputChange}
//               required
//               className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
//             />
//           </div>

//           {/* Registration fields */}
//           {!isLogin && (
//             <>
//               <div className="relative">
//                 <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
//                 <input
//                   type="text"
//                   name="full_name"
//                   placeholder="Full Name"
//                   value={formData.full_name}
//                   onChange={handleInputChange}
//                   required
//                   className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
//                 />
//               </div>

//               <div className="grid grid-cols-2 gap-4">
//                 <select
//                   name="department"
//                   value={formData.department}
//                   onChange={handleInputChange}
//                   required
//                   className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
//                 >
//                   <option value="">Select Department</option>
//                   <option value="Sales">Sales</option>
//                   <option value="Developer">Developer</option>
//                   <option value="Management">Management</option>
//                 </select>

//                 <input
//                   type="text"
//                   name="position"
//                   placeholder="Position"
//                   value={formData.position}
//                   onChange={handleInputChange}
//                   required
//                   className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
//                 />
//               </div>

//               <div className="relative">
//                 <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
//                 <input
//                   type="tel"
//                   name="phone"
//                   placeholder="Phone Number"
//                   value={formData.phone}
//                   onChange={handleInputChange}
//                   required
//                   className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
//                 />
//               </div>

//               <div className="relative">
//                 <label
//                   htmlFor="hire_date"
//                   className="block text-sm font-medium text-gray-700 mb-1"
//                 >
//                   Hire Date
//                 </label>
//                 <Calendar className="absolute left-3 top-10 w-5 h-5 text-gray-400" />
//                 <input
//                   id="hire_date"
//                   type="date"
//                   name="hire_date"
//                   value={formData.hire_date}
//                   onChange={handleInputChange}
//                   required
//                   className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
//                 />
//               </div>

//               <select
//                 name="role"
//                 value={formData.role}
//                 onChange={handleInputChange}
//                 className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
//               >
//                 <option value="employee">Employee</option>
//               </select>
//             </>
//           )}

//           <button
//             type="submit"
//             disabled={loading}
//             className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 transition"
//           >
//             {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
//           </button>
//         </form>

//         <div className="mt-6 text-center">
//           <button
//             type="button"
//             onClick={() => setIsLogin(!isLogin)}
//             className="text-blue-600 hover:text-blue-700 font-medium"
//           >
//             {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
//           </button>
//         </div>
//       </div>

//       {/* Modal for messages */}
//       <MessageModal
//         show={modal.show}
//         onClose={() => setModal({ ...modal, show: false })}
//         message={modal.message}
//         type={modal.type}
//         title={modal.title}
//       />
//     </div>
//   );
// }
